import type { AgentHealth } from '@tabel/shared/connector/protocol';
import {
  AGENTS,
  AGENT_LABEL,
  AGENT_MIN_VERSION,
  type AgentId,
  CODEX_PINNED_VERSION,
} from '@tabel/shared/connector/versions';
import type { ConnectorView } from '@tabel/shared/schemas/agent-client';
import { Check, Copy, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { Badge, Button, Callout, Card, CardHeader } from '../../components/ui';
import { checkAgent, installAgent } from '../../lib/agent-api';
import { useAgentSocket } from '../../lib/agent-ws';
import { ApiError } from '../../lib/errors';
import { AGENT_STATE_LABEL, LOGIN_LABEL, PC_LABEL, PC_T, PROGRAM, pcSay } from './texts';
import { clockSeconds, sinceText } from './time';

/*
 * Карточка «Агенты на <компьютер>» страницы «Мои ИИ-клиенты» (006, T055; US2; FR-007–FR-010, FR-060;
 * contracts/ui.md §4, мокап Clients.dc.html). Одна строка на агента: имя («Claude», «Codex», «pi» — без логотипов,
 * FR-041), бейдж состояния, строка версии и входа, одна кнопка по состоянию.
 *
 * | Состояние FR-008 | Бейдж | Под строкой | Кнопка |
 * |---|---|---|---|
 * | готов | «Готов» (ok) | — | «Проверить …» |
 * | не установлен | «Не установлен» | — | «Установить …» |
 * | нужна новая версия | «Нужна новая версия» (warn) | «нужна 2.1.286 или новее» | «Обновить …» |
 * | вход не выполнен, вход истёк | warn | команда входа со «Скопировать» | «Проверить …» |
 * | не подключён к табелю | warn | команда входа в табель по 005 | «Проверить …» |
 * | неизвестно | «Неизвестно» | когда станет известно | «Проверить …» |
 * | Codex на подписке ChatGPT | «Нужен вход ключом API» (warn) | пометка FR-060 и команда входа ключом | «Проверить Codex» |
 * | установка идёт | «Установка» (accent) | вывод установки | — |
 *
 * - Состояние приходит в списке компьютеров (`agents`, «не подключён к табелю» ставит сервер по 005) и в ответе
 *   «Проверить» (`POST …/check`); на экране — более позднее по `checkedAt`. Компьютер не в сети — все агенты
 *   «Неизвестно»: последнее состояние могло устареть.
 * - Опрос (research R9): пока карточка на экране, компьютер в сети и вкладка видна — список компьютеров заново каждые
 *   3 с (`onRefresh`); вкладка снова видна — сразу. Сервер на запрос списка сам перепроверяет агентов (не чаще раза в
 *   3 с) и шлёт WS `agents`. «Проверить» — проверка сразу, «Проверка…» на месте кнопки, ошибка — сообщение с причиной.
 * - Установка и обновление — командой коннектора (`POST …/install`, FR-009), не строкой от браузера. Ход установки —
 *   `installs` в списке компьютеров (виден и после перезагрузки страницы); новые строки — сообщения WS `install` этого
 *   компьютера. До первого списка с идущей установкой «Установка» держит сама страница. Одна установка за раз: кнопки
 *   «Установить» и «Обновить» других агентов недоступны. Установка не удалась — причина в последних строках вывода.
 * - Вход в агента табель не делает (FR-010): только команда для терминала со «Скопировать». Команда в несколько строк
 *   (вход pi: `pi`, затем `/login`) — первая строка для терминала, «Скопировать» копирует её; остальные —
 *   «Внутри pi введите». Пояснение входа — своё у агента (FR-060): Codex — ключ API OpenAI и где его взять; pi — любой
 *   поставщик, кроме подписки Claude; pi по подписке Claude — «Для Claude выберите агента Claude».
 * - Один интерфейс на 375/768/1280 px: до 900 px — имя, сведения и кнопка друг под другом; кнопки на телефоне — 40 px.
 */

export const AGENT_POLL_MS = 3000;
/** Срок установки (FR-009). */
const INSTALL_MAX_MS = 15 * 60_000;
/** Сколько строк вывода установки держать (как сервер, contracts/connector-protocol.md §2.4). */
const LOG_LINES = 200;
const SCREEN = 'MyAgents';

type ServerInstall = ConnectorView['installs'][number];

/** Строки WS `install` поверх снимка `base` из списка; другой снимок — строки уже в нём. */
interface Extra {
  base: ServerInstall | null;
  lines: string[];
}

/** Установку начала эта вкладка (или пришла строка WS), а список о ней ещё не знает. */
interface Bridge {
  /** `startedAt` установки агента в списке на этот момент: другой — список уже знает новую установку. */
  base: string | null;
  at: number;
}

type Tone = 'neutral' | 'accent' | 'ok' | 'warn';
interface View {
  tone: Tone;
  badge: string;
  /** Строка версии и входа. */
  status?: string;
  /** Пояснение. */
  text?: string;
  /** Пометка FR-060. */
  note?: string;
  command?: string;
  action?: { kind: 'check' | 'install' | 'update'; label: string };
}

const later = (a: AgentHealth | null, b: AgentHealth | null) =>
  !a ? b : !b ? a : Date.parse(b.checkedAt) > Date.parse(a.checkedAt) ? b : a;

function loginText(agent: AgentId, h: AgentHealth): string | null {
  if (!h.loginMethod) return null;
  return h.loginMethod === 'subscription' ? LOGIN_LABEL.subscription[agent] : LOGIN_LABEL[h.loginMethod];
}

/** Что показать в строке агента: бейдж, строка версии, пояснение, команда, одна кнопка (contracts/ui.md §4). */
function viewOf(agent: AgentId, h: AgentHealth | null, online: boolean): View {
  const name = AGENT_LABEL[agent];
  const prog = PROGRAM[agent];
  const check = { kind: 'check' as const, label: pcSay.check(name) };
  if (!online || !h || h.state === 'unknown')
    return {
      tone: 'neutral',
      badge: AGENT_STATE_LABEL.unknown,
      text: online ? PC_T.unknownOnline : PC_T.unknownOffline,
      action: check,
    };
  const login = loginText(agent, h);
  const blocked =
    agent === 'codex' &&
    h.flags.subscriptionBlocked === true &&
    (h.state === 'ready' ||
      h.state === 'not_linked' ||
      h.state === 'logged_out' ||
      h.state === 'login_expired');
  if (blocked)
    return {
      tone: 'warn',
      badge: PC_LABEL.subscriptionBlocked,
      status: pcSay.versionLine(
        prog,
        h.version,
        login,
        h.state === 'ready' ? true : h.state === 'not_linked' ? false : undefined,
      ),
      note: PC_T.codexSubscription,
      command: h.loginCommand ?? undefined,
      action: check,
    };
  switch (h.state) {
    case 'ready':
      return {
        tone: 'ok',
        badge: AGENT_STATE_LABEL.ready,
        status: pcSay.versionLine(prog, h.version, login, true),
        ...(agent === 'pi' && h.flags.claudeSubscription ? { text: PC_T.piClaudeReady } : {}),
        action: check,
      };
    case 'not_installed':
      return {
        tone: 'neutral',
        badge: AGENT_STATE_LABEL.not_installed,
        text: pcSay.notInstalled(prog),
        action: { kind: 'install', label: pcSay.install(name) },
      };
    case 'outdated': {
      // У Codex — точная версия своего экземпляра (research R6), у остальных — нижняя граница.
      const exact = agent === 'codex';
      const min = exact ? CODEX_PINNED_VERSION : h.minVersion || AGENT_MIN_VERSION[agent];
      return {
        tone: 'warn',
        badge: AGENT_STATE_LABEL.outdated,
        status: pcSay.outdated(prog, h.version, min, exact),
        action: { kind: 'update', label: pcSay.update(name) },
      };
    }
    case 'logged_out':
    case 'login_expired':
      return {
        tone: 'warn',
        badge: AGENT_STATE_LABEL[h.state],
        status: pcSay.versionLine(prog, h.version, null),
        text:
          agent === 'codex'
            ? PC_T.codexLogin
            : agent === 'pi'
              ? h.flags.claudeSubscription
                ? PC_T.piClaude
                : PC_T.piLogin
              : PC_T.loggedOut,
        command: h.loginCommand ?? undefined,
        action: check,
      };
    case 'not_linked':
      return {
        tone: 'warn',
        badge: AGENT_STATE_LABEL.not_linked,
        status: pcSay.versionLine(prog, h.version, login),
        text: agent === 'pi' ? PC_T.piNotLinked : PC_T.notLinked,
        command: h.linkCommand ?? undefined,
        action: check,
      };
  }
}

const without = <T,>(m: Partial<Record<AgentId, T>>, agent: AgentId) => {
  const n = { ...m };
  delete n[agent];
  return n;
};

export function AgentsCard({
  computer,
  tz,
  onRefresh,
  focusTick = 0,
  pollMs = AGENT_POLL_MS,
}: {
  computer: ConnectorView;
  tz: string;
  /** Список компьютеров заново: опрос раз в 3 с и после начала установки. */
  onRefresh: () => void;
  /** Растёт — фокус на заголовок карточки («Показать агентов», «Перейти к агентам»). */
  focusTick?: number;
  pollMs?: number;
}) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { showError } = useToast();
  const toastError = useRef(showError);
  toastError.current = showError;
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const pcRef = useRef(computer);
  pcRef.current = computer;
  const [checked, setChecked] = useState<Partial<Record<AgentId, AgentHealth>>>({});
  const [busy, setBusy] = useState<Partial<Record<AgentId, true>>>({});
  const [starting, setStarting] = useState<Partial<Record<AgentId, true>>>({});
  const [extra, setExtra] = useState<Partial<Record<AgentId, Extra>>>({});
  const [bridge, setBridge] = useState<Partial<Record<AgentId, Bridge>>>({});
  const online = computer.phase === 'active' && computer.online;
  const id = computer.id;

  const snapOf = (agent: AgentId, pc: ConnectorView = computer) =>
    pc.installs.find((i) => i.agent === agent) ?? null;
  const healthOf = (agent: AgentId) =>
    later(computer.agents.find((h) => h.agent === agent) ?? null, checked[agent] ?? null);
  /** Установка идёт: по списку или по этой вкладке, пока список о ней не знает. */
  const installOf = (agent: AgentId): { lines: string[] } | null => {
    if (!online) return null;
    const snap = snapOf(agent);
    const more = extra[agent];
    const tail = (base: ServerInstall | null) => (more && more.base === base ? more.lines : []);
    if (snap?.status === 'running' && Date.now() - Date.parse(snap.startedAt) < INSTALL_MAX_MS)
      return { lines: [...snap.tail, ...tail(snap)].slice(-LOG_LINES) };
    const b = bridge[agent];
    if (b && (snap?.startedAt ?? null) === b.base && Date.now() - b.at < INSTALL_MAX_MS)
      return { lines: tail(snap) };
    return null;
  };

  useEffect(() => {
    if (focusTick > 0) headingRef.current?.focus();
  }, [focusTick]);

  /** Установку начали здесь или на другой вкладке: «Установка» до первого списка, который о ней знает. */
  const bridgeFrom = (agent: AgentId) =>
    setBridge((prev) =>
      prev[agent]
        ? prev
        : { ...prev, [agent]: { base: snapOf(agent, pcRef.current)?.startedAt ?? null, at: Date.now() } },
    );

  // Новые строки вывода установки — сообщения WS `install` этого компьютера.
  useAgentSocket((e) => {
    if (e.op !== 'install' || e.connectorId !== pcRef.current.id) return;
    const snap = snapOf(e.agent, pcRef.current);
    setExtra((prev) => {
      const cur = prev[e.agent];
      const lines = cur && cur.base === snap ? cur.lines : [];
      return { ...prev, [e.agent]: { base: snap, lines: [...lines, ...e.lines].slice(-LOG_LINES) } };
    });
    if (snap?.status !== 'running') bridgeFrom(e.agent);
  });

  // Опрос: список компьютеров раз в 3 с, пока карточка на экране и вкладка видна (research R9).
  useEffect(() => {
    if (!online) return;
    const tick = () => {
      if (document.visibilityState !== 'hidden') refresh.current();
    };
    const timer = setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [online, pollMs]);

  const check = async (agent: AgentId) => {
    setBusy((b) => ({ ...b, [agent]: true }));
    try {
      const h = await checkAgent(id, agent);
      setChecked((c) => ({ ...c, [agent]: later(c[agent] ?? null, h) ?? h }));
    } catch (e) {
      // Частые нажатия (`429`) — не ошибка: состояние и так обновляется само.
      if (!(e instanceof ApiError && e.code === 'RATE_LIMITED'))
        toastError.current(e, { screen: SCREEN, action: 'check-agent' });
    } finally {
      setBusy((b) => without(b, agent));
    }
  };

  const install = async (agent: AgentId, action: 'install' | 'update') => {
    setStarting((s) => ({ ...s, [agent]: true }));
    try {
      await installAgent(id, agent, action);
      setBridge((prev) => ({
        ...prev,
        [agent]: { base: snapOf(agent, pcRef.current)?.startedAt ?? null, at: Date.now() },
      }));
      setExtra((prev) => without(prev, agent));
      refresh.current();
    } catch (e) {
      toastError.current(e, { screen: SCREEN, action: 'install-agent' });
    } finally {
      setStarting((s) => without(s, agent));
    }
  };

  const anyInstall = AGENTS.some((a) => installOf(a) !== null || starting[a]);
  const last = AGENTS.map(healthOf).reduce<AgentHealth | null>((m, h) => later(m, h), null);
  const description = online
    ? last
      ? `${PC_T.autoRefresh} ${pcSay.checkedAt(clockSeconds(last.checkedAt, tz))}`
      : PC_T.autoRefresh
    : `${pcSay.pcOfflineSince(sinceText(computer.lastSeenAt, tz))} ${PC_T.agentsUnknown}`;
  const title = pcSay.agentsOn(computer.name);

  return (
    <section aria-labelledby={headingId}>
      <Card>
        <CardHeader id={headingId} headingRef={headingRef} title={title} description={description} />
        <ul aria-label={title} className="divide-y divide-line border-t border-line">
          {AGENTS.map((agent) => {
            const snap = snapOf(agent);
            return (
              <AgentRow
                key={agent}
                agent={agent}
                view={viewOf(agent, healthOf(agent), online)}
                install={installOf(agent)}
                failed={online && snap?.status === 'failed' ? snap.tail : null}
                checking={busy[agent] === true}
                starting={starting[agent] === true}
                installBlocked={anyInstall}
                onCheck={() => void check(agent)}
                onInstall={(action) => void install(agent, action)}
              />
            );
          })}
        </ul>
      </Card>
    </section>
  );
}

function AgentRow({
  agent,
  view,
  install,
  failed,
  checking,
  starting,
  installBlocked,
  onCheck,
  onInstall,
}: {
  agent: AgentId;
  view: View;
  install: { lines: string[] } | null;
  /** Последняя установка не удалась: её последние строки. */
  failed: string[] | null;
  checking: boolean;
  starting: boolean;
  installBlocked: boolean;
  onCheck: () => void;
  onInstall: (action: 'install' | 'update') => void;
}) {
  const nameId = useId();
  const blockedId = useId();
  const name = AGENT_LABEL[agent];
  const v: View = install
    ? { tone: 'accent', badge: PC_LABEL.installing, text: pcSay.installing(PROGRAM[agent]) }
    : view;
  const busyText = install ? PC_LABEL.installRunning : checking ? PC_LABEL.checking : null;
  const act = v.action;
  // Итог неудачной установки — пока агента всё ещё нужно ставить или обновлять.
  const showFailed = !install && failed !== null && (act?.kind === 'install' || act?.kind === 'update');
  return (
    <li
      aria-labelledby={nameId}
      className="grid grid-cols-[176px_minmax(0,1fr)_160px] items-start gap-x-4 gap-y-2 px-6 py-3 max-tablet:grid-cols-1 max-sm:px-4"
    >
      <div className="flex flex-col items-start gap-1.5">
        <h3 id={nameId} className="text-[14px] leading-6 font-semibold text-ink">
          {name}
        </h3>
        <span data-badge>
          <Badge tone={v.tone} dot>
            {v.badge}
          </Badge>
        </span>
      </div>
      <div className="flex min-w-0 flex-col gap-2 pt-0.5 text-[13px] leading-normal text-ink-2">
        {v.status && <p className="break-words tabular-nums">{v.status}</p>}
        {v.text && <p>{v.text}</p>}
        {v.note && (
          <Callout tone="warn" icon={TriangleAlert}>
            {v.note}
          </Callout>
        )}
        {showFailed && act && (
          <Callout tone="warn" icon={TriangleAlert}>
            {pcSay.installFailed(act.label)}
          </Callout>
        )}
        {!install && v.command && <CommandBox command={v.command} agentName={name} />}
        {(install || showFailed) && (
          <pre
            role="log"
            aria-live="off"
            aria-label={PC_LABEL.installOutput}
            className="max-h-[120px] overflow-auto rounded-control border border-line bg-subtle px-3 py-2 font-mono text-[12px] leading-normal break-all whitespace-pre-wrap text-ink-2"
          >
            {(install?.lines ?? failed ?? []).join('\n')}
          </pre>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 max-tablet:justify-start">
        {busyText ? (
          <span role="status" className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2">
            <LoaderCircle
              size={14}
              strokeWidth={1.75}
              className="animate-spin motion-reduce:animate-none"
              aria-hidden
            />
            {busyText}
          </span>
        ) : act?.kind === 'check' ? (
          // До 900 px кнопка под сведениями: подпись — на одной линии с текстом строки.
          <Button variant="ghost" className="max-tablet:-ml-3" onClick={onCheck}>
            {act.label}
          </Button>
        ) : act ? (
          <div className="flex flex-col items-end gap-1 max-tablet:items-start">
            <Button
              variant="secondary"
              disabled={installBlocked || starting}
              aria-describedby={installBlocked && !starting ? blockedId : undefined}
              onClick={() => onInstall(act.kind === 'update' ? 'update' : 'install')}
            >
              {act.label}
            </Button>
            {/* Недоступная кнопка называет причину (ux.md §11). */}
            {installBlocked && !starting && (
              <span id={blockedId} className="text-[12px] text-ink-2">
                {PC_LABEL.otherInstall}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </li>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Команда для терминала и «Скопировать» (FR-010): команда видна целиком, её можно выделить и вручную. Команда в
 * несколько строк: первая — для терминала, «Скопировать» копирует её; остальные вводят внутри программы агента.
 */
/** Команда для терминала со «Скопировать»: первая строка — в терминал, остальные — внутри агента. */
export function CommandBox({ command, agentName }: { command: string; agentName: string }) {
  const [copy, setCopy] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const [first = '', ...inside] = command.split('\n');
  const run = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('буфер обмена недоступен');
      await navigator.clipboard.writeText(first);
      setCopy('copied');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopy('idle'), 2000);
    } catch {
      setCopy('failed');
    }
  };
  const copied = copy === 'copied';
  return (
    <div className="space-y-2">
      <div>
        <div className="flex items-center gap-2 rounded-control border border-line bg-subtle py-1 pr-1 pl-3 max-sm:flex-col max-sm:items-stretch max-sm:py-2 max-sm:pr-2">
          <figure aria-label={PC_LABEL.command} className="m-0 min-w-0 flex-1">
            <code className="block font-mono text-[12px] leading-normal break-all whitespace-pre-wrap text-ink">
              {first}
            </code>
          </figure>
          <Button
            icon={copied ? Check : Copy}
            // Название для чтения с экрана — с именем агента; после копирования — «Скопировано», как на экране.
            aria-label={copied ? undefined : pcSay.copyCommand(agentName)}
            onClick={() => void run()}
          >
            {copied ? PC_LABEL.copied : PC_LABEL.copy}
          </Button>
        </div>
        {copy === 'failed' && <p className="mt-1 text-[12.5px] text-warn">{PC_T.copyFailed}</p>}
      </div>
      {inside.length > 0 && (
        <>
          <p>{pcSay.inside(agentName)}</p>
          <figure
            aria-label={PC_LABEL.insideCommand}
            className="m-0 rounded-control border border-line bg-subtle px-3 py-1.5"
          >
            <code className="block font-mono text-[12px] leading-normal break-all whitespace-pre-wrap text-ink">
              {inside.join('\n')}
            </code>
          </figure>
        </>
      )}
    </div>
  );
}
